<template>
  <el-dialog
    :title="dialogTitle"
    :visible.sync="dialogVisible"
    width="700px"
    :close-on-click-modal="false"
    @close="handleClose"
  >
    <div class="config-form">
      <div class="form-item">
        <label class="item-title">前端名称:</label>
        <el-input :value="appInfo.name || '物流保险师'" disabled />
      </div>
      
      <div class="form-item">
        <label class="item-title">配置名称:</label>
        <el-input v-model="formData.name" placeholder="请输入配置名称" :disabled="isView" />
      </div>
      
      <div class="form-item">
        <label class="item-title">系统类型:</label>
        <div class="radio-group">
          <span 
            class="reset-radio" 
            :class="{ on: formData.os_type === 1 }" 
            @click="!isView && (formData.os_type = 1)"
          >
            Android
          </span>
          <span 
            class="reset-radio" 
            :class="{ on: formData.os_type === 2 }" 
            @click="!isView && (formData.os_type = 2)"
          >
            iOS
          </span>
        </div>
      </div>
      
      <div class="form-item upload-item">
        <label class="item-title">上传闪屏页:</label>
        <div class="upload-section">
          <div class="upload-btn" v-if="!isView">
            <input 
              type="file" 
              ref="fileInput"
              @change="handleFileUpload"
              accept="image/*"
              style="display: none"
            />
            <el-button type="primary" @click="$refs.fileInput.click()">上传</el-button>
          </div>
          <p class="upload-tip">图片尺寸：iOS 750*1020px Android 1080*1470px 图片格式：jpg/png</p>
          <div class="pic-preview" v-if="formData.splash">
            <div class="pic-item">
              <a :href="formData.splash" target="_blank">
                <img :src="formData.splash" width="100" alt="点击查看大图" />
              </a>
              <p class="del-pic" v-if="!isView" @click="formData.splash = ''">删 除</p>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <div slot="footer" class="dialog-footer" v-if="!isView">
      <el-button @click="handleClose">取消</el-button>
      <el-button type="primary" :loading="submitLoading" @click="handleSubmit">
        保存
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
import { saveStartup, getStartupInfo } from '@/api/custom-frontend'

export default {
  name: 'StartupEditDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    startupData: {
      type: Object,
      default: null
    },
    appInfo: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      submitLoading: false,
      uploadLoading: false,
      formData: {
        id: null,
        app_id: null,
        name: '',
        os_type: 1,
        type: 2, // 启动页类型
        splash: ''
      }
    }
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible
      },
      set(val) {
        this.$emit('update:visible', val)
      }
    },
    
    dialogTitle() {
      if (this.isView) return '查看'
      return this.startupData ? '编辑' : '添加'
    },
    
    isView() {
      return this.startupData && this.startupData.action === 'view'
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.initFormData()
      }
    }
  },
  methods: {
    async initFormData() {
      if (this.startupData && this.startupData.id) {
        // 编辑模式，获取详细信息
        try {
          const res = await getStartupInfo({ id: this.startupData.id })
          if (res && res.err_code === 0) {
            this.formData = {
              id: res.data.id,
              app_id: res.data.app_id,
              name: res.data.name,
              os_type: res.data.os_type,
              type: res.data.type,
              splash: res.data.config ? res.data.config.splash : ''
            }
          }
        } catch (error) {
          console.error('获取启动页详情失败:', error)
        }
      } else {
        // 新增模式
        this.formData = {
          id: null,
          app_id: this.appInfo.id,
          name: '',
          os_type: 1,
          type: 2,
          splash: ''
        }
      }
    },
    
    async handleFileUpload(event) {
      const file = event.target.files[0]
      if (!file) return
      
      // 文件类型验证
      const isImage = file.type.startsWith('image/')
      if (!isImage) {
        this.$message.error('只能上传图片文件')
        return
      }
      
      // 文件大小验证
      const isLt5M = file.size / 1024 / 1024 < 5
      if (!isLt5M) {
        this.$message.error('图片大小不能超过5MB')
        return
      }
      
      this.uploadLoading = true
      
      try {
        const formData = new FormData()
        formData.append('file', file)
        
        // 使用原生请求上传文件
        const token = this.$store.getters.token
        const response = await fetch(`${process.env.VUE_APP_BASE_URL}/res/file/upload`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        })
        
        const result = await response.json()
        
        if (result.err_code === 0) {
          this.formData.splash = result.data
          this.$message.success('上传成功')
        } else {
          this.$message.error(result.err_msg || '上传失败')
        }
      } catch (error) {
        console.error('上传失败:', error)
        this.$message.error('上传失败')
      } finally {
        this.uploadLoading = false
        // 清空文件输入
        this.$refs.fileInput.value = ''
      }
    },
    
    checkData() {
      if (!this.formData.name) {
        this.$message.error('请输入配置名称')
        return false
      }
      if (!this.formData.splash) {
        this.$message.error('请上传闪屏页')
        return false
      }
      return true
    },
    
    async handleSubmit() {
      if (!this.checkData()) return
      
      this.submitLoading = true
      
      try {
        const submitData = {
          app_id: this.formData.app_id,
          name: this.formData.name,
          os_type: this.formData.os_type,
          type: this.formData.type,
          image_config: {
            splash: this.formData.splash
          }
        }
        
        if (this.formData.id) {
          submitData.id = this.formData.id
        }
        
        const res = await saveStartup(submitData)
        if (res && res.err_code === 0) {
          this.$message.success('保存成功')
          this.$emit('refresh')
          this.handleClose()
        } else {
          this.$message.error(res.err_msg || '保存失败')
        }
      } catch (error) {
        console.error('保存启动页失败:', error)
        this.$message.error('保存失败')
      } finally {
        this.submitLoading = false
      }
    },
    
    handleClose() {
      this.dialogVisible = false
      this.formData = {
        id: null,
        app_id: null,
        name: '',
        os_type: 1,
        type: 2,
        splash: ''
      }
    }
  }
}
</script>

<style lang="less" scoped>
.config-form {
  padding: 20px;
  
  .form-item {
    margin-bottom: 20px;
    
    .item-title {
      display: inline-block;
      width: 85px;
      font-weight: normal;
    }
    
    /deep/ .el-input {
      width: 430px;
      
      .el-input__inner {
        height: 30px;
        line-height: 30px;
        border: 1px solid #95B8E7;
        border-radius: 3px;
      }
    }
    
    .radio-group {
      display: inline-block;
      
      .reset-radio {
        position: relative;
        display: inline-block;
        width: 80px;
        height: 26px;
        line-height: 26px;
        border: 1px solid #eee;
        border-radius: 3px;
        text-align: center;
        cursor: pointer;
        margin-right: 10px;
        
        &.on {
          border-color: #95B8E7;
          background-color: #f0f9ff;
        }
        
        &:hover {
          border-color: #95B8E7;
        }
      }
    }
  }
  
  .upload-item {
    .upload-section {
      display: inline-block;
      vertical-align: top;
      width: calc(100% - 85px);
      
      .upload-btn {
        margin-bottom: 10px;
      }
      
      .upload-tip {
        color: red;
        font-size: 12px;
        margin-bottom: 15px;
      }
      
      .pic-preview {
        .pic-item {
          display: inline-block;
          position: relative;
          margin-right: 15px;
          
          img {
            border: 1px solid #ddd;
            border-radius: 4px;
          }
          
          .del-pic {
            position: absolute;
            left: 0;
            bottom: 0;
            width: 100%;
            height: 20px;
            line-height: 20px;
            background: rgba(0, 0, 0, 0.7);
            color: #fff;
            text-align: center;
            font-size: 12px;
            cursor: pointer;
            opacity: 0;
            transition: opacity 0.3s;
          }
          
          &:hover .del-pic {
            opacity: 1;
          }
        }
      }
    }
  }
}
</style>